import React, { useState, useEffect } from "react";
import {
    Card, CardBody, Container, Spinner, Row, Col, Input, InputGroup
} from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { H3 } from "../../AbstractElements";
import { MapPin, Battery, Calendar } from 'react-feather';

interface LocationReportData {
    id: number;
    bystander_id: number;
    booking_id: number;
    latitude: number;
    longitude: number;
    battery_level: number;
    remarks: string;
    created_at: string;
    bystander?: {
        id: number;
        name: string;
    };
    booking?: {
        id: number;
        status: string;
        date: string;
        address: string;
        user_id: number;
        user?: {
            id: number;
            name: string;
        }
    };
}

export default function BystanderLocationLogs() {
    const [data, setData] = useState<LocationReportData[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortBy, setSortBy] = useState("created_at");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, sortBy, sortDir, searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, currentPage, perPage, sortBy, sortDir]);

    const fetchData = async (page: number, size: number, sort: string, direction: string, search: string) => {
        setLoading(true);
        try {
            const response: any = await apiPost(`bystander/location_reports?page=${page}`, {
                per_page: size,
                sort_by: sort,
                sort_direction: direction,
                search: search
            });
            if (response.status) {
                setData(response.data || []);
                setTotalRows(response.total || 0);
            } else {
                toast.error("Failed to load location logs");
            }
        } catch (error) {
            toast.error("Failed to load location logs");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        setSortBy(column.sortField || "created_at");
        setSortDir(sortDirection);
    };

    const columns = [
        {
            name: "Sl.No",
            cell: (row: LocationReportData, index: number) => <span>{index + 1 + (currentPage - 1) * perPage}</span>,
            sortable: false,
            width: "10%",
        },
        {
            name: <div><span className="fw-bold">Bystander</span> Name</div>,
            selector: (row: LocationReportData) => row.bystander?.name || "Unknown",
            sortable: true,
            sortField: "bystander_name",
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column">
                    <span className="fw-bold text-dark">{row.bystander?.name || "Unknown"}</span>
                </div>
            ),
            width: "19%",
        },
        {
            name: <div><span className="fw-bold">Location</span> Coordinates</div>,
            selector: (row: LocationReportData) => `${row.latitude}, ${row.longitude}`,
            sortable: true,
            sortField: "latitude",
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column gap-1 align-items-start py-1">
                    <div className="d-flex flex-column">
                        <span className="small fw-bold" style={{ fontSize: '11px', color: '#4b5563' }}>Lat: {Number(row.latitude || 0).toFixed(6)}</span>
                        <span className="small fw-bold" style={{ fontSize: '11px', color: '#4b5563' }}>Lng: {Number(row.longitude || 0).toFixed(6)}</span>
                    </div>
                    <button
                        onClick={() => window.open(`https://www.google.com/maps?q=${row.latitude},${row.longitude}`, '_blank')}
                        style={{
                            border: '1px solid #0d9488',
                            color: '#0d9488',
                            backgroundColor: '#f0fdfa',
                            borderRadius: '6px',
                            padding: '2px 8px',
                            fontSize: '11px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            transition: 'all 0.2s',
                            marginTop: '2px'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#0d9488';
                            e.currentTarget.style.color = '#ffffff';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = '#f0fdfa';
                            e.currentTarget.style.color = '#0d9488';
                        }}
                    >
                        <MapPin size={11} /> View on Map
                    </button>
                </div>
            ), width: "19%",
        },
        {
            name: "Date and Time",
            selector: (row: LocationReportData) => row.created_at,
            sortable: true,
            sortField: "created_at",
            cell: (row: LocationReportData) => (
                <div className="d-flex flex-column">
                    <span className="fw-bold">{new Date(row.created_at).toLocaleDateString()}</span>
                    <span className="small text-muted">{new Date(row.created_at).toLocaleTimeString()}</span>
                </div>
            ), width: "17%",
        },
        {
            name: "Battery",
            selector: (row: LocationReportData) => row.battery_level,
            sortable: true,
            sortField: "battery_level",
            cell: (row: LocationReportData) => {
                let color = "text-success";
                if (row.battery_level < 20) color = "text-danger";
                else if (row.battery_level < 50) color = "text-warning";
                return (
                    <div className="d-flex align-items-center gap-1">
                        <Battery size={14} className={color} />
                        <span className={`fw-bold ${color}`}>{row.battery_level}%</span>
                    </div>
                );
            }, width: "17%",
        },
        {
            name: "Remarks",
            selector: (row: LocationReportData) => row.remarks,
            sortable: true,
            sortField: "remarks",
            cell: (row: LocationReportData) => {
                const remarks = row.remarks || "";
                const isOff = remarks.toLowerCase().includes("off");
                const isOn = remarks.toLowerCase().includes("on");

                let color = "#4b5563"; // neutral grey
                let bg = "#f3f4f6";
                if (isOff) {
                    color = "#dc2626"; // red
                    bg = "#fee2e2";
                } else if (isOn) {
                    color = "#16a34a"; // green
                    bg = "#dcfce7";
                }

                return (
                    <span style={{
                        color: color,
                        backgroundColor: bg,
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'inline-block'
                    }}>
                        {remarks || "No remarks"}
                    </span>
                );
            }, width: "18%",

        }
    ];


    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: { fontSize: '15px', fontWeight: '600', paddingLeft: '16px', paddingRight: '16px' },
        },
        rows: {
            style: { minHeight: '50px' },
        },
    };


    return (
        <div className="page-body">
            <ToastContainer />
            <Breadcrumbs pageTitle="Location Disable Logs" parent="Bystander Management" title="Location Disable Logs" />
            <Container fluid={true}>
                <Card>
                    <CardBody>

                        <Row className="mb-3 justify-content-end">
                            <Col md={4} lg={3}>
                                <InputGroup className="shadow-sm border-0">
                                    <Input
                                        placeholder="Search by bystander..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control-sm"
                                        style={{ borderRadius: '8px' }}
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm="12">
                                <Card className="shadow-sm border-0">


                                    <CardBody className="p-0">
                                        <div className="table-responsive">
                                            <DataTable
                                                columns={columns}
                                                data={data}
                                                progressPending={loading}
                                                progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                                noDataComponent={
                                                    <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "50vh" }}>
                                                        <Card className="shadow-sm border-0 text-center" style={{ maxWidth: "320px", width: "100%" }}>
                                                            <CardBody>
                                                                <h6 className="fw-semibold mb-1">No Location Logs</h6>
                                                                <small className="text-muted">No instances of disabled location tracking found.</small>
                                                            </CardBody>
                                                        </Card>
                                                    </div>
                                                }
                                                pagination
                                                paginationServer
                                                paginationTotalRows={totalRows}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}
                                                sortServer
                                                onSort={handleSort}
                                                customStyles={customStyles}
                                                highlightOnHover
                                                pointerOnHover
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
}





